import { ReactNode, useState } from 'react'
import {
  BottomDrawer,
  breakpointQueries,
  Button,
  Modal,
  useMediaQuery,
} from '@systemeio/components'
import { LogOut01Icon, XCloseIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import useUser from 'shared/hooks/use-user'
import useIsUserMember from '../hooks/use-is-user-member'
import { CommunityInterface } from '../types/community-interface'

interface CommunityCartProps {
  community: CommunityInterface
  onLeaveCommunity: (community: CommunityInterface) => void
  isFetching: boolean
}

const CommunityCart = ({
  community,
  onLeaveCommunity,
  isFetching,
}: CommunityCartProps): ReactNode => {
  const { t } = useTranslation()
  const { isOwner } = useUser()

  const [opened, setOpened] = useState<boolean>(false)
  const isMobile = useMediaQuery(breakpointQueries.isMobile, { initializeWithValue: false })

  const { isUserMember } = useIsUserMember(community.path, () => {}, false)

  const title = t('community.leave_modal_tittle', { name: community.name })

  const fields = (
    <p className="text-center text-base text-text-primary">
      {t('community.leave_modal_sub_title')}
    </p>
  )

  const renderAction = (buttonClassName?: string): ReactNode => (
    <>
      <Button variant="secondary" onClick={() => setOpened(false)} className={buttonClassName}>
        {t('confirmation_modal.cancel')}
      </Button>
      <Button
        variant="destructive"
        isLoading={isFetching}
        onClick={() => onLeaveCommunity(community)}
        className={buttonClassName}
      >
        {t('confirmation_modal.leave')} <LogOut01Icon className="size-5" />
      </Button>
    </>
  )

  const action = renderAction()

  return (
    <>
      <div className="group relative w-full max-w-[385px] overflow-hidden rounded-lg bg-bg-primary">
        {!isOwner && isUserMember && (
          <Button
            variant="ghost-gray"
            size="icon-md"
            className="absolute top-3 right-3 hidden group-hover:block"
            onClick={() => setOpened(true)}
          >
            <LogOut01Icon className="size-5" />
          </Button>
        )}
        {community.imagePath ? (
          <img src={community.imagePath} alt={community.name} className="w-full" />
        ) : (
          <img src="/demo_community_image.png" alt={community.name} />
        )}
        <div className="px-5 py-6">
          <Link
            href={`/community/${community.path}`}
            className="mb-3 block text-2xl font-semibold text-text-primary"
            shallow
          >
            {community.name}
          </Link>
        </div>
        <div className="flex justify-end bg-bg-page/30 px-5 py-3">
          <Button
            variant="primary"
            render={<LinkWithoutPrefetch href={`/community/${community.path}`} />}
          >
            {t('community.button_enter_community.title')}
          </Button>
        </div>
      </div>
      {isMobile ? (
        <BottomDrawer.Root
          open={opened}
          onOpenChange={value => {
            if (!value) setOpened(false)
          }}
        >
          <BottomDrawer.Popup>
            <BottomDrawer.Header className="flex-row items-start gap-2.5">
              <BottomDrawer.Title className="min-w-0 flex-1">{title}</BottomDrawer.Title>
              <BottomDrawer.Close
                aria-label="Close"
                className="shrink-0"
                render={<Button variant="ghost-gray" size="icon-sm" />}
              >
                <XCloseIcon />
              </BottomDrawer.Close>
            </BottomDrawer.Header>
            <BottomDrawer.Content className="flex flex-col gap-6">{fields}</BottomDrawer.Content>
            <BottomDrawer.Footer>
              <div className="flex w-full gap-3">{renderAction('min-w-[90px] flex-1')}</div>
            </BottomDrawer.Footer>
          </BottomDrawer.Popup>
        </BottomDrawer.Root>
      ) : (
        <Modal.Root
          open={opened}
          onOpenChange={value => {
            if (!value) setOpened(false)
          }}
        >
          <Modal.Popup>
            <Modal.Header className="pr-16">
              <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 py-6">
              {fields}
            </div>
            <Modal.Footer>{action}</Modal.Footer>
          </Modal.Popup>
        </Modal.Root>
      )}
    </>
  )
}

export default CommunityCart
